import { Core } from "@faire/design-tokens";
import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { Button } from "@faire/web--source/slate/Button";
import { Tooltip } from "@faire/web--source/slate/Tooltip";
import { Typography } from "@faire/web--source/slate/Typography";
import { useEffectEvent } from "@faire/web--source/ui/hooks/useEffectEvent";
import { Instance, createPopper } from "@popperjs/core";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";

import {
  dottedClassname,
  tooltipWrapperHide,
  tooltipWrapperShow,
} from "./styles.css";

export interface IDottedTooltipProps {
  text: string;
  tooltipTitle?: string;
  tooltipText?: string;
  tooltipCTA?: string;
  tooltipCTALink?: string;
  width?: number;
  position?: "top" | "left" | "right" | "bottom";
}

export const DottedTooltip: React.FC<IDottedTooltipProps> = ({
  text,
  tooltipTitle,
  tooltipText,
  tooltipCTA,
  tooltipCTALink,
  width = 350,
  position = "top",
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [popperInstance, setPopperInstance] = useState<Instance>();

  useEffect(() => {
    if (tooltipRef.current && wrapperRef.current) {
      setPopperInstance(
        createPopper(wrapperRef.current, tooltipRef.current, {
          placement: position,
        })
      );
    }
  }, []);

  useEffect(() => {
    return () => {
      popperInstance?.destroy();
    };
  }, [popperInstance]);

  const handleMouseOver = useEffectEvent(() => {
    setIsVisible(true);
    popperInstance?.setOptions({
      modifiers: [{ name: "eventListeners", enabled: true }],
    });
    popperInstance?.update();
  });

  const handleMouseOut = useEffectEvent(() => {
    setIsVisible(false);
    popperInstance?.setOptions({
      modifiers: [{ name: "eventListeners", enabled: false }],
    });
  });

  const contents = useMemo(() => {
    return [
      {
        title: tooltipTitle,
        body: tooltipText,
      },
    ];
  }, [tooltipTitle, tooltipText]);

  return (
    <div onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
      <Typography
        className={dottedClassname}
        variant="paragraphSansMedium"
        ref={wrapperRef}
      >
        {text}
      </Typography>
      {ReactDOM.createPortal(
        <div
          ref={tooltipRef}
          className={isVisible ? tooltipWrapperShow : tooltipWrapperHide}
        >
          <Tooltip
            position={position}
            width={width}
            isOpen
            targetNode={<div />}
            contents={contents}
            action={
              <Button
                variant="tertiary"
                tertiaryColor={Core.surface.inverse}
                linkTo={tooltipCTALink}
              >
                {tooltipCTA}
              </Button>
            }
          />
        </div>,
        getWindowOrThrow().document.body
      )}
    </div>
  );
};
